import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { fileLabel } from '../../lib/fileName';
import {
  Modal, Button, Input, Select, Tag, Alert, Space, message, type TagTone,
} from '../../ui';
import {
  Play, ArrowUpCircle, CornerUpLeft, CheckCircle2, Lock, Ban, Reply,
  ThumbsUp, ThumbsDown, UserPlus, XCircle,
} from 'lucide-react';
import { request } from '../../api/client';
import { FileUploadField } from '../../components/FileUploadField';

/**
 * THE COMPLAINT WORKFLOW, described once for the whole frontend.
 *
 * Every act on a complaint used to be the same thing — set `status` to a new string — so the UI had
 * a bare dropdown and the server had no idea WHY anything moved. Both screens now drive off this one
 * registry instead, which means the board and the detail page cannot drift: they offer the same
 * actions, from the same states, and collect the same mandatory remark.
 *
 * A remark is MANDATORY on every act that is a decision somebody may be answerable for — closing,
 * rejecting, escalating, sending back, resolving, disputing, withdrawing, and the customer's reply.
 * It is optional on the routine steps (take up, assign, accept), where the actor, the timestamp and
 * the status already say everything a reader needs and a forced sentence would only produce filler.
 *
 * The registry is a MIRROR of the backend, never the authority. Each action still calls its own
 * endpoint, which re-checks the permission, the transition and the remark. What lives here is only
 * which button to show — so a stale build can annoy someone with a button that 400s, but it can
 * never let them do something the server would refuse.
 */
export type ComplaintStatus =
  | 'OPEN' | 'IN_PROGRESS' | 'AWAITING_INFO' | 'ESCALATED'
  | 'RESOLVED' | 'CLOSED' | 'REOPENED' | 'REJECTED' | 'CANCELLED';

export const TERMINAL_STATUSES: ComplaintStatus[] = ['CLOSED', 'REJECTED', 'CANCELLED'];

export const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  AWAITING_INFO: 'Awaiting Info',
  ESCALATED: 'Escalated',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  REOPENED: 'Reopened',
  REJECTED: 'Rejected',
  CANCELLED: 'Withdrawn',
};

export const STATUS_TONE: Record<string, TagTone> = {
  OPEN: 'blue',
  IN_PROGRESS: 'gold',
  // Amber, not red: the complaint is parked with the CUSTOMER and its SLA clock is stopped, so
  // colouring it like a breach would point the reader at the wrong problem.
  AWAITING_INFO: 'gold',
  ESCALATED: 'red',
  RESOLVED: 'green',
  CLOSED: 'neutral',
  REOPENED: 'purple',
  REJECTED: 'neutral',
  CANCELLED: 'neutral',
};

/** One line explaining what each state actually means to whoever is waiting. */
export const STATUS_MEANING: Record<string, string> = {
  OPEN: 'Raised, not yet picked up by anyone.',
  IN_PROGRESS: 'Someone owns it and is working on it.',
  AWAITING_INFO: 'Waiting on the complainant for something. The SLA clock is paused.',
  ESCALATED: 'Pushed to someone senior — either deliberately or because it passed its SLA.',
  RESOLVED: 'A fix has been stated. Waiting for the complainant to accept it, or for someone to close it.',
  CLOSED: 'Finished. Only a Super Admin can send it back for review.',
  REOPENED: 'Sent back for review by a Super Admin after being closed.',
  REJECTED: 'Not a valid complaint — a duplicate, or a claim with nothing behind it.',
  CANCELLED: 'Withdrawn by the complainant, or closed because the evidence we asked for never arrived.',
};

/**
 * The same line, but NAMING the person when the record is to hand.
 *
 * "Someone owns it and is working on it" is the honest sentence for a status chip standing on its
 * own — a board cell, a tooltip — where all that is known is the word. On the complaint itself the
 * app knows exactly who owns it, who stated the fix and who closed it, and saying "someone" there
 * makes the reader go and look it up two cards further down. Falls back to the generic sentence
 * whenever the name is missing, so an unassigned IN_PROGRESS complaint still reads correctly.
 */
export interface ComplaintPeople {
  assignedToName?: string | null; assignedToRole?: string | null;
  resolvedByName?: string | null; resolvedByRole?: string | null;
  closedByName?: string | null; closedByRole?: string | null;
}

/**
 * The status line broken into parts, so the PERSON in it can carry a role badge.
 *
 * The sentence is deliberately built to end on the name (`… now with Vikram RSM`) rather than around
 * it (`Vikram RSM holds it now`): the house convention puts the coloured role pill immediately after
 * a person's name, and a badge stranded at the end of a sentence reads as punctuation. `suffix`
 * carries anything that still has to be said after the name.
 */
export function statusLineParts(
  status: string,
  c?: ComplaintPeople | null,
): { prefix: string; name?: string | null; role?: string | null; suffix?: string } {
  const who = (n?: string | null) => (n && String(n).trim() ? String(n).trim() : null);
  const owner = who(c?.assignedToName);
  switch (status) {
    case 'IN_PROGRESS':
      return owner
        ? { prefix: 'Being worked on by', name: owner, role: c?.assignedToRole }
        : { prefix: 'Nobody owns it yet, but it is being worked on.' };
    case 'ESCALATED':
      return owner
        ? { prefix: 'Pushed to someone senior — now with', name: owner, role: c?.assignedToRole }
        : { prefix: STATUS_MEANING.ESCALATED };
    case 'RESOLVED': {
      const by = who(c?.resolvedByName);
      return by
        ? { prefix: 'A fix was stated by', name: by, role: c?.resolvedByRole,
            suffix: 'Waiting for the complainant to accept it, or for someone else to close it.' }
        : { prefix: STATUS_MEANING.RESOLVED };
    }
    case 'CLOSED': {
      const by = who(c?.closedByName);
      return by
        ? { prefix: 'Finished — closed by', name: by, role: c?.closedByRole,
            suffix: 'Only a Super Admin can send it back for review.' }
        : { prefix: STATUS_MEANING.CLOSED };
    }
    default:
      return { prefix: STATUS_MEANING[status] ?? '' };
  }
}

/** The same line as plain text — for a tooltip, where there is nowhere to put a badge. */
export function statusMeaning(status: string, c?: ComplaintPeople | null): string {
  const { prefix, name, suffix } = statusLineParts(status, c);
  if (!name) return prefix;
  return `${prefix} ${name}.${suffix ? ` ${suffix}` : ''}`;
}

export const SEVERITY_LABEL: Record<string, string> = { LOW: 'Low', MED: 'Medium', HIGH: 'High', CRIT: 'Critical' };
export const SEVERITY_TONE: Record<string, TagTone> = { LOW: 'blue', MED: 'gold', HIGH: 'gold', CRIT: 'red' };

/** What the trail's `action` column means in plain words. */
export const ACTION_LABEL: Record<string, string> = {
  CREATED: 'Raised',
  ASSIGNED: 'Assigned',
  REASSIGNED: 'Reassigned',
  STARTED: 'Work started',
  ESCALATED: 'Escalated',
  AUTO_ESCALATED: 'Escalated automatically (SLA breach)',
  SENT_BACK: 'Sent back to the complainant',
  INFO_PROVIDED: 'Complainant replied',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  ACCEPTED: 'Resolution accepted',
  DISPUTED: 'Resolution disputed',
  REJECTED: 'Rejected as invalid',
  WITHDRAWN: 'Withdrawn',
  AUTO_CLOSED: 'Closed automatically (no response)',
  AUTO_CANCELLED: 'Closed automatically (evidence never provided)',
  REVERTED: 'Reopened by a Super Admin',
  MIGRATED: 'Recorded before the trail existed',
};

// ─────────────────────────────────────────────────────────────────────────────
// Action registry
// ─────────────────────────────────────────────────────────────────────────────

export interface ActionField {
  name: string;
  label: string;
  type: 'textarea' | 'text' | 'user' | 'attachments' | 'rating';
  required?: boolean;
  placeholder?: string;
  help?: string;
}

export interface ComplaintAction {
  key: string;
  label: string;
  icon: ReactNode;
  /** PATCH path suffix, appended to `/complaints/{id}`. */
  path: string;
  /** ANY-of. Empty means the action is not permission-gated (it is gated by ownership instead). */
  perms: string[];
  /** Only offered from these statuses. */
  from: ComplaintStatus[];
  /** Offered only to the complainant side (raiser, or the retailer/farmer it was raised for). */
  complainantOnly?: boolean;
  danger?: boolean;
  primary?: boolean;
  /** Shown above the fields — what pressing this actually does. */
  note?: string;
  fields?: ActionField[];
  /** Success toast. */
  done: string;
}

const REASON = (label: string, placeholder: string): ActionField =>
  ({ name: 'reason', label, type: 'textarea', required: true, placeholder });

export const COMPLAINT_ACTIONS: ComplaintAction[] = [
  {
    key: 'start',
    /*
     * "Take it up" sat directly above "Escalate" and read as its sibling — take it UP the chain —
     * when it means the opposite: keep it and start work. Named for what it does to the complaint.
     */
    label: 'Start work',
    icon: <Play size={16} />,
    path: 'start',
    perms: ['COMPLAINT.ASSIGN', 'COMPLAINT.RESOLVE'],
    from: ['OPEN', 'REOPENED', 'AWAITING_INFO', 'ESCALATED'],
    primary: true,
    note: 'Takes ownership and marks the complaint In Progress. If nobody owns it yet, it becomes yours. It stays with you — it is not passed to anyone.',
    fields: [{
      name: 'remarks', label: 'Note (optional)', type: 'textarea',
      placeholder: 'e.g. Calling the retailer today to arrange a pickup of the damaged stock.',
    }],
    done: 'Marked in progress',
  },
  {
    key: 'assign',
    label: 'Assign',
    icon: <UserPlus size={16} />,
    path: 'assign',
    perms: ['COMPLAINT.ASSIGN'],
    from: ['OPEN', 'IN_PROGRESS', 'AWAITING_INFO', 'ESCALATED', 'REOPENED'],
    note: 'Hand the complaint to whoever will actually resolve it. Only Sales Heads, Admins and Super Admins can resolve, so only they can be assigned.',
    fields: [
      { name: 'assignedToUserId', label: 'Assign to', type: 'user', required: true },
      {
        name: 'remarks', label: 'Note for the new owner (optional)', type: 'textarea',
        placeholder: 'e.g. Dealer has called twice; needs the credit note raised before Friday.',
      },
    ],
    done: 'Assigned',
  },
  {
    key: 'send_back',
    label: 'Send back',
    icon: <CornerUpLeft size={16} />,
    path: 'send-back',
    perms: ['COMPLAINT.ASSIGN', 'COMPLAINT.RESOLVE', 'COMPLAINT.ESCALATE'],
    from: ['OPEN', 'IN_PROGRESS', 'ESCALATED', 'REOPENED'],
    note: 'Ask the complainant for what is missing — a clearer photo, the batch code, the invoice. Both the person who raised it and the dealer/farmer it was raised for can answer. The SLA clock pauses until they do.',
    fields: [{
      name: 'reason', label: 'What they need to provide', type: 'textarea', required: true,
      placeholder: 'e.g. A photo showing the batch code printed on the pack — the one attached is too blurred to read.',
      help: 'Be specific. This is the whole message they receive.',
    }],
    done: 'Sent back to the complainant',
  },
  {
    key: 'escalate',
    label: 'Escalate',
    icon: <ArrowUpCircle size={16} />,
    path: 'escalate',
    perms: ['COMPLAINT.ESCALATE'],
    from: ['OPEN', 'IN_PROGRESS', 'AWAITING_INFO', 'REOPENED'],
    danger: true,
    note: 'Pushes the complaint to the next level and puts it in the Approvals hub for someone senior.',
    fields: [REASON('Why this needs escalating', 'What is stuck, and what you need from someone senior')],
    done: 'Escalated',
  },
  {
    key: 'resolve',
    label: 'Resolve',
    icon: <CheckCircle2 size={16} />,
    path: 'resolve',
    perms: ['COMPLAINT.RESOLVE'],
    from: ['IN_PROGRESS', 'ESCALATED', 'REOPENED'],
    primary: true,
    note: 'State what was actually done. The complainant sees this and can accept it or dispute it. Resolving does NOT close the complaint — someone else has to.',
    fields: [
      {
        name: 'notes', label: 'Resolution — what was done', type: 'textarea', required: true,
        placeholder: 'e.g. Replaced the 2 leaking 1L bottles from batch GB-2451 on 3 Sep; credit note CN/00214 issued for the shortfall.',
      },
      { name: 'attachments', label: 'Evidence (optional)', type: 'attachments' },
    ],
    done: 'Resolved',
  },
  {
    key: 'close',
    label: 'Close',
    icon: <Lock size={16} />,
    path: 'close',
    perms: ['COMPLAINT.CLOSE'],
    from: ['RESOLVED'],
    primary: true,
    note: 'Confirms the complaint is genuinely over. You cannot close a complaint you resolved yourself — that check is the point of the separate step.',
    fields: [{
      name: 'remarks', label: 'Closure remarks', type: 'textarea', required: true,
      placeholder: 'e.g. Verified with the retailer on 3 Sep — replacement received and credit note reflected in their ledger.',
      help: 'Mandatory. This is the permanent record of why the complaint was ended.',
    }],
    done: 'Closed',
  },
  {
    key: 'reject',
    label: 'Reject',
    icon: <Ban size={16} />,
    path: 'reject',
    perms: ['COMPLAINT.CLOSE'],
    from: ['OPEN', 'IN_PROGRESS', 'AWAITING_INFO', 'ESCALATED', 'REOPENED'],
    danger: true,
    note: 'For a duplicate, or a claim with nothing behind it. It is recorded as rejected, NOT as resolved, so it never counts as a fix.',
    fields: [REASON('Why this is not a valid complaint', 'e.g. Duplicate of CMP/257841748, raised for the same batch on the same day.')],
    done: 'Rejected as invalid',
  },
  // ── Complainant-side ──────────────────────────────────────────────────────
  {
    key: 'respond',
    label: 'Reply with what was asked for',
    icon: <Reply size={16} />,
    path: 'respond',
    perms: [],
    complainantOnly: true,
    from: ['AWAITING_INFO'],
    primary: true,
    note: 'Answer the request and attach the evidence. The complaint goes straight back to the team and the SLA clock restarts.',
    fields: [
      { name: 'message', label: 'Your reply', type: 'textarea', required: true, placeholder: 'What you are sending, and anything else that helps' },
      { name: 'attachments', label: 'Attach the photo / document asked for', type: 'attachments' },
    ],
    done: 'Reply sent',
  },
  {
    key: 'accept',
    label: 'Accept resolution',
    icon: <ThumbsUp size={16} />,
    path: 'accept',
    perms: [],
    complainantOnly: true,
    from: ['RESOLVED'],
    primary: true,
    note: 'Confirms the problem is sorted. This closes the complaint.',
    fields: [
      { name: 'rating', label: 'How was it handled?', type: 'rating' },
      {
        name: 'remarks', label: 'Anything to add (optional)', type: 'textarea',
        placeholder: 'e.g. Replacement bottles received on 4 Sep and the credit note is showing.',
      },
    ],
    done: 'Thanks — the complaint is closed',
  },
  {
    key: 'dispute',
    label: 'Not fixed',
    icon: <ThumbsDown size={16} />,
    path: 'dispute',
    perms: [],
    complainantOnly: true,
    from: ['RESOLVED'],
    danger: true,
    note: 'Sends the complaint back to the team with a fresh deadline.',
    fields: [REASON('What is still wrong', 'Tell us what is still not right')],
    done: 'Sent back to the team',
  },
  {
    key: 'withdraw',
    label: 'Withdraw',
    icon: <XCircle size={16} />,
    path: 'withdraw',
    perms: [],
    complainantOnly: true,
    from: ['OPEN', 'IN_PROGRESS', 'AWAITING_INFO', 'ESCALATED', 'REOPENED'],
    danger: true,
    note: 'Closes the complaint as withdrawn. It is not counted as something we fixed.',
    fields: [REASON('Why you no longer need this pursued', 'e.g. The missing carton turned up the next day.')],
    done: 'Complaint withdrawn',
  },
];

/** Who is looking — only what the self-close rule needs. */
export interface ComplaintViewer { userId?: string | null; isSuperAdmin?: boolean }

/** The server's own sentence when a resolver tries to close (complaint.module.ts close(), SELF_CLOSE). */
export const SELF_CLOSE_MESSAGE =
  'You resolved this complaint, so someone else has to close it. Ask an Admin, or let the complainant accept the resolution.';

/**
 * Did THIS viewer state the fix? Mirrors the server's close() rule: the resolver may not also close
 * it, and a Super Admin is exempt. The server still decides — this only stops offering a dead end.
 */
export function isOwnResolution(
  complaint: { resolvedBy?: string | number | null } | null | undefined,
  me?: ComplaintViewer,
): boolean {
  if (!complaint || !me?.userId || me.isSuperAdmin) return false;
  return complaint.resolvedBy != null && String(complaint.resolvedBy) === String(me.userId);
}

/**
 * The actions this viewer can take on this complaint, in a fixed order.
 *
 * `viewer.isComplainantSide` comes from the SERVER — the "is this mine to answer" rule covers the
 * raiser AND the dealer/farmer the complaint was raised for, and re-deriving that in the browser
 * would produce a weaker copy that gets it wrong for on-behalf-of complaints.
 */
export function actionsFor(
  complaint: { status: string; resolvedBy?: string | number | null; viewer?: { isComplainantSide?: boolean } } | null | undefined,
  hasPerm: (p: string) => boolean,
  me?: ComplaintViewer,
): ComplaintAction[] {
  if (!complaint) return [];
  const status = complaint.status as ComplaintStatus;
  const mine = !!complaint.viewer?.isComplainantSide;
  return COMPLAINT_ACTIONS.filter((a) => {
    if (!a.from.includes(status)) return false;
    // The server always refuses Close to whoever resolved it (SELF_CLOSE), so it is not offered.
    if (a.key === 'close' && isOwnResolution(complaint, me)) return false;
    if (a.complainantOnly) return mine;
    return a.perms.length === 0 || a.perms.some(hasPerm);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// The one modal every action goes through
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Collects an action's fields and calls its endpoint.
 *
 * Deliberately ONE modal for every action rather than a bespoke dialog each: the thing that must not
 * vary is that a required remark is actually required, and that the server's refusal (a missing
 * attachment, resolving your own complaint, an invalid transition) is shown to the user rather than
 * swallowed into a generic "Action failed".
 */
export function ComplaintActionModal({
  action, complaint, open, onClose, onDone, assignees,
}: {
  action: ComplaintAction | null;
  complaint: { id: string; complaintNo?: string; categoryRule?: any } | null;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
  /**
    * Candidates for the `user` field. Only fetched by callers that offer Assign.
    *
    * `label` is rich (a name plus a coloured role pill), so `searchLabel` carries the plain text the
    * type-to-search filter reads — matching against a React element finds nothing.
    */
   assignees?: { value: string; label: React.ReactNode; searchLabel?: string }[];
}) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setValues({}); setFiles([]); setError(null); };
  const close = () => { reset(); onClose(); };

  const run = useConfirmedMutation({ title: action ? `${action.label}?` : "Record this action?", content: "It is recorded against your name in the complaint trail.", okText: "Yes, continue" }, {
    mutationFn: () => request({
      url: `/complaints/${complaint!.id}/${action!.path}`,
      method: 'PATCH',
      data: { ...values, ...(files.length ? { attachments: files } : {}) },
    }),
    onSuccess: () => {
      message.success(action!.done);
      // The nav badge and the approvals queue both count complaints, so both go stale on every act.
      for (const key of [['complaints'], ['complaint'], ['complaint-history'], ['complaint-summary'],
        ['badges'], ['approvals'], ['approval-queue']]) {
        qc.invalidateQueries({ queryKey: key });
      }
      onDone?.();
      close();
    },
    onError: (e: any) => setError(
      e?.response?.data?.error?.message ?? e?.response?.data?.message ?? 'Could not complete that action.'),
  });

  if (!action || !complaint) return null;

  const missing = (action.fields ?? []).filter((f) => {
    if (!f.required) return false;
    if (f.type === 'attachments') return files.length === 0;
    return !String(values[f.name] ?? '').trim();
  });
  /*
   * A required remark has to be a sentence, not a keystroke. The server enforces 3 characters; saying
   * so here stops the round-trip that would otherwise just bounce back "Reason is required".
   */
  const tooShort = (action.fields ?? []).some(
    (f) => f.required && f.type === 'textarea' && String(values[f.name] ?? '').trim().length > 0
      && String(values[f.name] ?? '').trim().length < 3);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={`${action.label} · ${complaint.complaintNo ?? ''}`}
      width={560}
      footer={
        <>
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button
            variant={action.danger ? 'danger' : 'primary'}
            loading={run.isPending}
            disabled={missing.length > 0 || tooShort}
            onClick={() => { setError(null); run.mutate(); }}
          >
            {action.label}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {action.note && <Alert type="info" description={action.note} />}
        {error && <Alert type="error" title="That did not go through" description={error} />}

        {(action.fields ?? []).map((f) => (
          <div key={f.name}>
            <label className="mb-1 block type-body-medium">
              {f.label}
              {f.required && <span className="ml-1 text-danger-600">*</span>}
            </label>

            {f.type === 'textarea' && (
              <Input.TextArea
                rows={3}
                placeholder={f.placeholder}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )}
            {f.type === 'text' && (
              <Input
                placeholder={f.placeholder}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )}
            {f.type === 'user' && (
              <Select
                showSearch
                /* The rich label is a React node; the plain text to match on is `searchLabel`. */
                optionFilterProp="searchLabel"
                placeholder="Search people who can resolve complaints…"
                value={values[f.name] ?? undefined}
                onChange={(val) => setValues((v) => ({ ...v, [f.name]: val }))}
                options={assignees ?? []}
              />
            )}
            {f.type === 'rating' && (
              <Space size={4}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n} star${n === 1 ? '' : 's'}`}
                    className="text-2xl leading-none"
                    style={{ color: n <= Number(values[f.name] ?? 0) ? 'var(--color-harvest-400)' : 'var(--gb-text-muted)' }}
                    onClick={() => setValues((v) => ({ ...v, [f.name]: n }))}
                  >
                    ★
                  </button>
                ))}
              </Space>
            )}
            {f.type === 'attachments' && (
              <div>
                {files.length > 0 && (
                  <Space size={6} wrap className="mb-2">
                    {files.map((u, i) => (
                      <Tag key={u} closable onClose={() => setFiles((a) => a.filter((x) => x !== u))}>
                        {fileLabel(u, { index: i + 1 })}
                      </Tag>
                    ))}
                  </Space>
                )}
                <FileUploadField
                  category="attachment"
                  buttonLabel="Attach file"
                  preview={false}
                  value=""
                  onChange={(url) => { if (url) setFiles((a) => [...a, url]); }}
                />
              </div>
            )}

            {f.help && <div className="mt-1 text-xs text-muted">{f.help}</div>}
          </div>
        ))}
      </div>
    </Modal>
  );
}
